import {
    AddressType,
    NotificationOrigin,
    NotificationType,
    getNotificationOriginTranslationId,
    getNotificationTypeTranslationId,
} from '@synergy/models';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormControlLabel, Grid, Switch, styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolModal, PoolSelect, PoolTextField, PoolTypography, SelectOption } from '@synergy/components';
import { fields } from '@synergy/utils';

import { NotificationSettingFormValues } from '../../models/notification-form-values';
import { notificationsModalValidationSchema } from './notifications-modal-valildation-schema';

const HeaderWithToggle = styled(PoolTypography)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: theme.spacing(2),
}));

const Toggle = styled(FormControlLabel)(({ theme }) => ({
    marginBottom: -10,
    marginRight: 0,
}));

interface NotificationsModalProps {
    notification: NotificationSettingFormValues | null;
    onClose: () => void;
    onSubmit: (values: NotificationSettingFormValues) => void;
}

export const fieldNames = fields<NotificationSettingFormValues>();

export const NotificationsModal = (props: NotificationsModalProps) => {
    const { notification, onClose, onSubmit } = props;

    const notificationTypeOptions: SelectOption[] = Object.values(NotificationType).map<SelectOption>(value => ({
        value: value,
        displayValue: <FormattedMessage id={getNotificationTypeTranslationId(value)} />,
    }));

    const notificationOrderTypeOptions: SelectOption[] = Object.values(NotificationOrigin).map<SelectOption>(value => ({
        value: value,
        displayValue: <FormattedMessage id={getNotificationOriginTranslationId(value)} />,
    }));

    if (!notification) {
        return null;
    }

    const getActionChildren = (formikProps: FormikProps<NotificationSettingFormValues>) => {
        return [
            <PoolButton key="1" styleType="firstLevel" variant="text" onClick={onClose}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton key="2" styleType="firstLevel" type="submit" onClick={() => formikProps.submitForm()}>
                <FormattedMessage id={notification.id ? 'app.common.save' : 'app.common.add'} />
            </PoolButton>,
        ];
    };

    return (
        <Formik initialValues={notification} onSubmit={onSubmit} validationSchema={notificationsModalValidationSchema}>
            {formikProps => (
                <PoolModal
                    title={
                        <FormattedMessage
                            id={
                                notification.id
                                    ? 'app.contactsAndOrganizations.detail.notifications.edit'
                                    : 'app.contactsAndOrganizations.detail.notifications.add'
                            }
                        />
                    }
                    isVisible={!!notification}
                    actionChildren={getActionChildren(formikProps)}
                    handleClose={onClose}
                >
                    <Grid container={true} spacing={2}>
                        <Grid item={true} xs={12}>
                            <Field name={fieldNames.notificationType}>
                                {({ field, form }: FieldProps<AddressType>) => (
                                    <PoolSelect
                                        label={
                                            <FormattedMessage id="app.contactsAndOrganizations.detail.notifications.notificationType" />
                                        }
                                        fullWidth={true}
                                        options={notificationTypeOptions}
                                        disallowDefaultValue={true}
                                        value={field.value}
                                        name={field.name}
                                        noSorting={true}
                                        onValueChange={value => form.setFieldValue(field.name, value)}
                                    />
                                )}
                            </Field>
                        </Grid>
                        <Grid item={true} xs={12}>
                            <Field name={fieldNames.notificationOrigin}>
                                {({ field, form }: FieldProps<AddressType>) => (
                                    <PoolSelect
                                        label={
                                            <FormattedMessage id="app.contactsAndOrganizations.detail.notifications.orderType" />
                                        }
                                        fullWidth={true}
                                        options={notificationOrderTypeOptions}
                                        disallowDefaultValue={true}
                                        value={field.value}
                                        name={field.name}
                                        noSorting={true}
                                        onValueChange={value => form.setFieldValue(field.name, value)}
                                    />
                                )}
                            </Field>
                        </Grid>
                        <Grid item={true} xs={12}>
                            <HeaderWithToggle styleType="sectionTitle" component="div">
                                <Field name={fieldNames.overrideEmail}>
                                    {({ field }: FieldProps<boolean>) => (
                                        <Toggle
                                            control={
                                                <Switch
                                                    color="primary"
                                                    checked={field.value}
                                                    onBlur={field.onBlur}
                                                    onChange={field.onChange}
                                                    name={field.name}
                                                />
                                            }
                                            label={
                                                <PoolTypography typography="h6" component="div">
                                                    <FormattedMessage id="app.contactsAndOrganizations.detail.notifications.override" />
                                                </PoolTypography>
                                            }
                                        />
                                    )}
                                </Field>
                            </HeaderWithToggle>
                            <Field name={fieldNames.recipientEmail}>
                                {({ field, meta }: FieldProps<AddressType>) => (
                                    <PoolTextField
                                        {...field}
                                        fullWidth={true}
                                        required={true}
                                        error={meta.touched && !!meta.error}
                                        helperText={
                                            meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                        }
                                        label={<FormattedMessage id="app.common.email" />}
                                        disabled={!formikProps.values.overrideEmail}
                                    />
                                )}
                            </Field>
                        </Grid>
                    </Grid>
                </PoolModal>
            )}
        </Formik>
    );
};
